import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import StatsCard from '../../components/admin/StatsCard';
import {
  ActiveUsersIcon,
  BalanceIcon,
  CompletedIcon,
  OrdersIcon,
  PendingIcon,
  ProcessingIcon,
  RevenueIcon,
  TransactionsIcon,
  UsersIcon,
} from '../../components/admin/DashboardIcons';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useGetStatsQuery } from '../../store/api/dashboardApi';
import { formatAmountExact } from '../../utils/amountFormatter';

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetStatsQuery();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">{t('common.error') || 'Error loading dashboard statistics'}</p>
      </div>
    );
  }

  const stats = data?.data;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-brand-accent mb-2">
          {t('dashboard.title') || 'Dashboard'}
        </h1>
        <p className="text-neutral-medium">
          {t('dashboard.subtitle') || 'Overview of your system'}
        </p>
      </div>

      {/* Profits Button */}
      <div className="mb-6">
        <Link
          to="/admin/profits"
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-colors duration-200"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{t('dashboard.viewProfits') || 'عرض الأرباح'}</span>
        </Link>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Users Stats */}
          <StatsCard
            title={t('dashboard.users.total') || 'Total Users'}
            value={stats.users.total}
            icon={<UsersIcon />}
            color="blue"
            delay={0}
          />
          <StatsCard
            title={t('dashboard.users.active') || 'Active Users'}
            value={stats.users.active}
            icon={<ActiveUsersIcon />}
            color="green"
            delay={100}
          />
          <StatsCard
            title={t('dashboard.users.totalBalance') || 'Total Balance'}
            value={`$${formatAmountExact(stats.users.totalBalance)}`}
            icon={<BalanceIcon />}
            color="luxury"
            delay={200}
          />

          {/* Transactions Stats */}
          <StatsCard
            title={t('dashboard.transactions.total') || 'Total Transactions'}
            value={stats.transactions.total}
            icon={<TransactionsIcon />}
            color="purple"
            delay={300}
          />
          <StatsCard
            title={t('dashboard.transactions.recentRevenue') || 'Recent Revenue'}
            value={`$${formatAmountExact(stats.transactions.recentRevenue)}`}
            icon={<RevenueIcon />}
            color="luxury"
            delay={400}
          />
          <StatsCard
            title={t('dashboard.transactions.pending') || 'Pending'}
            value={stats.transactions.pending}
            icon={<PendingIcon />}
            color="orange"
            delay={500}
          />

          {/* Orders Stats */}
          <StatsCard
            title={t('dashboard.orders.total') || 'Total Orders'}
            value={stats.orders.total}
            icon={<OrdersIcon />}
            color="blue"
            delay={600}
          />
          <StatsCard
            title={t('dashboard.orders.completed') || 'Completed'}
            value={stats.orders.byStatus.completed}
            icon={<CompletedIcon />}
            color="green"
            delay={700}
          />
          <StatsCard
            title={t('dashboard.orders.processing') || 'Processing'}
            value={stats.orders.byStatus.processing}
            icon={<ProcessingIcon />}
            color="yellow"
            delay={800}
          />
        </div>
      )}
    </div>
  );
}
